import { AuthProvider, useAuth } from '../auth/AuthContext';
import { AppProviders } from './providers';
import { ToastProvider } from '../components/Toast';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { AppRouter } from './router';
import { VConnectLoader } from '../components/VConnectBrand';
import { SelfApprovalPrompt } from '../components/SelfApprovalPrompt';

function AuthGate() {
  const { loading, user } = useAuth();

  // While /auth/me is in flight, or if it decided to redirect to
  // /auth/login (in which case the browser is already navigating away
  // and user stays null), show nothing rather than a flash of the
  // real UI with no data behind it.
  if (loading || !user) {
    return (
      <div className="auth-loading">
        <VConnectLoader label="Opening VConnect…" />
      </div>
    );
  }

  return <AppRouter />;
}

/**
 * The public careers portal (/careers/*), the new branded /login
 * page, and the partner-portal pages (/partner/*) must NEVER go
 * through the internal Entra auth gate below - AuthProvider's own
 * effect unconditionally redirects to /login for anyone without an
 * internal staff session, which is exactly wrong for a page an
 * anonymous visitor or external partner is meant to reach with no
 * VConnect staff account at all. This was a real bug: the router
 * itself correctly placed /careers/* as a sibling outside AppShell's
 * layout, but this outer wrapper in App.tsx applies before the router
 * ever matches a path, so every visitor - internal or public - was
 * being gated here first. Checked by raw pathname since this decision
 * has to happen before AuthProvider's effect fires, i.e. before React
 * Router is even mounted.
 */
function isPublicPath(): boolean {
  const path = window.location.pathname;
  return path.startsWith('/careers') || path === '/login' || path.startsWith('/partner') || path === '/suppliers' || path.startsWith('/suppliers/')
    // BAM B5 point of sale: must open with no network. It is not public - it checks the
    // staff session itself whenever there is signal, works offline only behind the
    // officer's PIN, and every API call is still authorised on the server.
    || path === '/m/pos' || path.startsWith('/m/pos/');
}

export function App() {
  const isPublic = isPublicPath();

  return (
    <ErrorBoundary>
      <AppProviders>
        <ToastProvider>
          {isPublic ? <AppRouter /> : (
            <AuthProvider>
              <AuthGate />
              <SelfApprovalPrompt />
            </AuthProvider>
          )}
        </ToastProvider>
      </AppProviders>
    </ErrorBoundary>
  );
}
